import type {
  CrossroadsLog,
  LogGroup,
  LogOverviewRow,
} from '@/api/hooks/useIntegrationLogs'
import { fetchClient } from '@/api/typedClient'
import { useTenantStore } from '@/stores/tenantStore'
import type {
  GetDetailRowDataParams,
  RowClassParams,
  RowClickedEvent,
} from 'ag-grid-community'
import { useCallback, useState } from 'react'
import { AuditModal } from './AuditModal/AuditModal'
import {
  type AuditTarget,
  getAutoGroupColumnDef,
  getDetailColumnDefs,
  getLatestLeafData,
} from './DetailGridRenderer'

/**
 * Master-detail wiring shared by every log overview grid (order, supply
 * plan, ...) — expanding a row fetches that group's raw logs from the same
 * generic /crossroads/logging/group endpoint (keyed by an opaque group id,
 * not order-specific) and renders them via DetailGridRenderer.
 */
export function useLogDetailGrid(options?: {
  onLogSelected?: (log: CrossroadsLog, logGroupId: string | null) => void
  /** Whether the detail grid should group rows by Event (default true). */
  groupByEventId?: boolean
  /** Variant for the Status chip shown on a warning log (default 'warning'),
   * see getDetailColumnDefs in DetailGridRenderer.tsx. */
  warningVariant?: 'warning' | 'partial_success'
}) {
  const onLogSelected = options?.onLogSelected
  const groupByEventId = options?.groupByEventId ?? true
  const warningVariant = options?.warningVariant
  const { selectedTenant } = useTenantStore()
  const [auditTarget, setAuditTarget] = useState<AuditTarget | null>(null)

  const openAudit = useCallback((target: AuditTarget) => {
    setAuditTarget(target)
  }, [])

  const getRowId = useCallback((params: { data: LogOverviewRow }) => {
    return params.data.id
  }, [])

  const detailCellRendererParams = useCallback(
    (masterParams: { data: LogOverviewRow }) => ({
      detailGridOptions: {
        context: { logGroupId: masterParams.data.id, openAudit },
        columnDefs: getDetailColumnDefs({ groupByEventId, warningVariant }),
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
          menuTabs: ['filterMenuTab'],
        },
        autoGroupColumnDef: getAutoGroupColumnDef(),
        suppressHeaderMenuButton: true,
        sideBar: {
          toolPanels: ['columns'],
        },
        groupDefaultExpanded: 0,
        rowGroupPanelShow: 'always',
        initialGroupOrderComparator: (params) => {
          const aRows = params.nodeA.allLeafChildren ?? []
          const bRows = params.nodeB.allLeafChildren ?? []
          const aMax = Math.max(
            ...aRows.map((n) => new Date(n.data?.date ?? 0).getTime()),
          )
          const bMax = Math.max(
            ...bRows.map((n) => new Date(n.data?.date ?? 0).getTime()),
          )
          return bMax - aMax
        },
        getRowHeight: (params: { node?: { group?: boolean } }) =>
          params.node?.group ? 40 : 36,
        getRowId: (params: { data: CrossroadsLog }) => params.data._id!,
        getRowStyle: (params: RowClassParams<CrossroadsLog>) => {
          // A retried or overridden log has been superseded, so the whole row
          // is grayed out -- the grayscale filter is what drains the Status/
          // Severity/Issue chips of their color too, which opacity alone
          // leaves reading as live. Read through getLatestLeafData for the
          // same reason the Status column does: `params.data` is undefined on
          // a group row, and with Event grouping on (the default) every
          // top-level row here is a group. Matching the column's condition
          // also means a row never grays out without a chip saying why.
          const log = getLatestLeafData(params)
          if (log?.retried || (log?.overridden && log.status !== 'succeeded')) {
            return {
              textDecoration: 'line-through',
              filter: 'grayscale(1)',
              opacity: 0.5,
            }
          }
          // Marks a leaf row as sitting *inside* an expanded Event group, so
          // it only means anything while grouping is on -- ungrouped, every
          // row is a leaf and the whole grid ends up striped.
          if (groupByEventId && params.node?.group === false) {
            return {
              borderLeft: '3px solid var(--theme-primary, #1677ff)',
              background:
                'color-mix(in srgb, var(--theme-primary, #1677ff) 4%, transparent)',
              fontSize: '12px',
              color: 'var(--theme-text-secondary, #666)',
            }
          }
          return undefined
        },
        rowSelection: 'single' as const,
        suppressRowClickSelection: true,
        onRowClicked: (event: RowClickedEvent<CrossroadsLog>) => {
          let log = event.data
          if (!log && event.node?.group) {
            const children = event.node.allLeafChildren ?? []
            log = children
              .map((n) => n.data)
              .filter((d): d is CrossroadsLog => !!d)
              .sort(
                (a, b) =>
                  new Date(b.date!).getTime() - new Date(a.date!).getTime(),
              )[0]
          }
          if (log) {
            onLogSelected?.(log, event.context?.logGroupId ?? null)
          }
        },
      },
      getDetailRowData: async (
        params: GetDetailRowDataParams<LogOverviewRow>,
      ) => {
        const groupId = params.data.id
        if (!groupId) {
          params.successCallback([])
          return
        }

        try {
          const { data, error } = await fetchClient.POST(
            '/crossroads/logging/group',
            {
              params: {
                query: { group_id: groupId },
                header: {
                  'X-Tenant-Name': selectedTenant?.name ?? 'Gravitate',
                },
              },
            },
          )

          if (error || !data) {
            console.error('Failed to fetch detail logs:', error)
            params.successCallback([])
            return
          }

          // Schema types response as unknown; cast to LogGroup
          const group = data as unknown as LogGroup
          params.successCallback(group.logs ?? [])
        } catch (err) {
          console.error('Error fetching detail logs:', err)
          params.successCallback([])
        }
      },
    }),
    [
      selectedTenant?.name,
      openAudit,
      onLogSelected,
      groupByEventId,
      warningVariant,
    ],
  )

  const auditModalElement = auditTarget ? (
    <AuditModal
      open
      onClose={() => setAuditTarget(null)}
      title={auditTarget.title}
      logGroupId={auditTarget.logGroupId}
      sagaId={auditTarget.sagaId}
      routing={auditTarget.routing}
    />
  ) : null

  return { getRowId, detailCellRendererParams, auditModalElement }
}
